import {
  createContext,
  useContext,
  useState,
  type ElementType,
  type FormHTMLAttributes,
  type ReactNode,
} from "react";

type Values = Record<string, string>;
type Errors = Partial<Record<string, string>>;

interface FormState {
  values: Values;
  errors: Errors;
  touched: Record<string, boolean>;
  setFieldValue: (name: string, value: string) => void;
  handleChange: (event: { target: { name: string; value: string } }) => void;
  handleBlur: (event: { target: { name: string } }) => void;
  handleSubmit: (event?: { preventDefault?: () => void }) => void;
}

const FormContext = createContext<FormState | null>(null);

const useFormContext = (): FormState => {
  const form = useContext(FormContext);
  if (!form) throw new Error("Field and ErrorMessage must be inside SimpleForm");
  return form;
};

interface SimpleFormProps {
  initialValues: Values;
  /** Returns a message per field; an undefined message means valid. */
  validate: (values: Values) => Errors;
  onSubmit: (values: Values) => void;
  children: (form: FormState) => ReactNode;
}

/**
 * The slice of Formik the booking forms used: values start from
 * initialValues (read once), every change, blur and submit re-validates, a
 * field's error shows once it has been touched (blurred, or any submit), and
 * submit touches every field and calls onSubmit only when nothing is invalid.
 */
export function SimpleForm({ initialValues, validate, onSubmit, children }: SimpleFormProps) {
  const [state, setState] = useState({
    values: initialValues,
    errors: {} as Errors,
    touched: {} as Record<string, boolean>,
  });

  const errorsFor = (values: Values): Errors =>
    Object.fromEntries(Object.entries(validate(values)).filter(([, message]) => message));

  const setFieldValue = (name: string, value: string) =>
    setState((prev) => {
      const values = { ...prev.values, [name]: value };
      return { ...prev, values, errors: errorsFor(values) };
    });

  const form: FormState = {
    ...state,
    setFieldValue,
    handleChange: ({ target }) => setFieldValue(target.name, target.value),
    handleBlur: ({ target }) =>
      setState((prev) => ({
        ...prev,
        touched: { ...prev.touched, [target.name]: true },
        errors: errorsFor(prev.values),
      })),
    handleSubmit: (event) => {
      event?.preventDefault?.();
      const errors = errorsFor(state.values);
      setState((prev) => ({
        ...prev,
        errors,
        touched: Object.fromEntries(Object.keys(prev.values).map((name) => [name, true])),
      }));
      if (Object.keys(errors).length === 0) onSubmit(state.values);
    },
  };

  return <FormContext.Provider value={form}>{children(form)}</FormContext.Provider>;
}

export function Form(props: FormHTMLAttributes<HTMLFormElement>) {
  const form = useFormContext();
  return <form action="#" {...props} onSubmit={form.handleSubmit} />;
}

type FieldProps = { name: string; as?: ElementType } & Record<string, unknown>;

export function Field({ name, as: Tag = "input", ...props }: FieldProps) {
  const form = useFormContext();
  return (
    <Tag
      name={name}
      value={form.values[name] ?? ""}
      onChange={form.handleChange}
      onBlur={form.handleBlur}
      {...props}
    />
  );
}

export function ErrorMessage({
  name,
  component: Tag = "div",
  className,
}: {
  name: string;
  component?: ElementType;
  className?: string;
}) {
  const { touched, errors } = useFormContext();
  return touched[name] && errors[name] ? <Tag className={className}>{errors[name]}</Tag> : null;
}
